import { useEffect, useRef, useState } from 'react';
import { FileUp } from 'lucide-react';
import { useLocation, useNavigate } from 'react-router';
import { useTranslation } from 'react-i18next';
import { offerDocument, readFile } from './documents';
import './import.css';

const hasFiles = (e: DragEvent) => Array.from(e.dataTransfer?.types ?? []).includes('Files');

/**
 * On the web a bill can be dropped anywhere in the app: the page shows
 * where it goes, and the import screen opens with it. (The import screen's
 * own drop zone handles drops there.)
 */
export function DocumentDrop() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const location = useLocation();
  const [over, setOver] = useState(false);
  const depth = useRef(0);
  const onImport = location.pathname === '/import';

  useEffect(() => {
    if (onImport) return;
    const enter = (e: DragEvent) => {
      if (!hasFiles(e)) return;
      depth.current++;
      setOver(true);
    };
    const leave = (e: DragEvent) => {
      if (!hasFiles(e)) return;
      depth.current = Math.max(0, depth.current - 1);
      if (depth.current === 0) setOver(false);
    };
    const overFn = (e: DragEvent) => {
      if (hasFiles(e)) e.preventDefault();
    };
    const drop = (e: DragEvent) => {
      if (!hasFiles(e)) return;
      e.preventDefault();
      depth.current = 0;
      setOver(false);
      const file = e.dataTransfer?.files[0];
      if (!file) return;
      void readFile(file, 'drop').then((r) => {
        offerDocument(r);
        navigate('/import');
      });
    };
    window.addEventListener('dragenter', enter);
    window.addEventListener('dragleave', leave);
    window.addEventListener('dragover', overFn);
    window.addEventListener('drop', drop);
    return () => {
      window.removeEventListener('dragenter', enter);
      window.removeEventListener('dragleave', leave);
      window.removeEventListener('dragover', overFn);
      window.removeEventListener('drop', drop);
    };
  }, [navigate, onImport]);

  if (!over || onImport) return null;
  return (
    <div className="drop-overlay" data-testid="drop-overlay">
      <div className="drop-overlay-card">
        <FileUp size={28} aria-hidden />
        <span>{t('import.dropAnywhere')}</span>
      </div>
    </div>
  );
}
